// Dónde plantar un panel flotante pegado a su control. Vive fuera del componente porque las cards
// del portal recortan con `overflow-hidden`: el panel se portalea a `body` y necesita coordenadas.
import { useLayoutEffect, useState, type RefObject } from 'react';

/** Alto máximo por omisión; por debajo de eso el panel se voltea hacia arriba si abajo no cabe. */
const DEFAULT_MAX_HEIGHT = 320;
/** Piso del panel cuando el control queda pegado a un borde del viewport. */
const PANEL_MIN_HEIGHT = 160;
/** Aire entre el control y el panel. */
const PANEL_GAP = 4;

/** Posición del panel en coordenadas del viewport: se ancla por arriba o por abajo, nunca por ambos. */
export interface PanelAnchor {
    left: number;
    width: number;
    top?: number;
    bottom?: number;
    maxHeight: number;
}

function isSameAnchor(a: PanelAnchor, b: PanelAnchor): boolean {
    return (
        a.left === b.left &&
        a.width === b.width &&
        a.top === b.top &&
        a.bottom === b.bottom &&
        a.maxHeight === b.maxHeight
    );
}

/**
 * Mide el control y devuelve dónde plantar el panel, volteándolo hacia arriba si abajo no cabe.
 * `maxHeight` es el alto que el panel puede llegar a ocupar, con el que se decide ese volteo.
 */
export function useAnchoredPanel(
    open: boolean,
    anchorRef: RefObject<HTMLElement | null>,
    maxHeight: number = DEFAULT_MAX_HEIGHT,
): PanelAnchor | null {
    const [anchor, setAnchor] = useState<PanelAnchor | null>(null);

    useLayoutEffect(() => {
        if (!open) return undefined;
        const reposition = (): void => {
            const rect = anchorRef.current?.getBoundingClientRect();
            if (!rect) return;
            const below = window.innerHeight - rect.bottom - PANEL_GAP;
            const above = rect.top - PANEL_GAP;
            const flipUp = below < maxHeight && above > below;
            const next: PanelAnchor = {
                left: rect.left,
                width: rect.width,
                top: flipUp ? undefined : rect.bottom + PANEL_GAP,
                bottom: flipUp ? window.innerHeight - rect.top + PANEL_GAP : undefined,
                maxHeight: Math.max(flipUp ? above : below, PANEL_MIN_HEIGHT),
            };
            setAnchor((prev) => (prev && isSameAnchor(prev, next) ? prev : next));
        };
        reposition();
        // El scroll ocurre en el `<main>` del shell, no en `window`: sin captura el panel no lo sigue.
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [open, anchorRef, maxHeight]);

    return anchor;
}
